import React from 'react'

interface AuthBackgroundProps {
  children: React.ReactNode
}

// Dark theme CSS variables - forces dark mode regardless of user preference
const darkThemeVars: React.CSSProperties = {
  // @ts-expect-error CSS custom properties
  '--background': 'oklch(0.12 0.03 235)',
  '--foreground': 'oklch(0.92 0.01 235)',
  '--card': 'oklch(0.16 0.03 235)',
  '--card-foreground': 'oklch(0.92 0.01 235)',
  '--popover': 'oklch(0.16 0.03 235)',
  '--popover-foreground': 'oklch(0.92 0.01 235)',
  '--primary': 'oklch(0.78 0.15 168)',
  '--primary-foreground': 'oklch(0.12 0.03 235)',
  '--secondary': 'oklch(0.22 0.03 235)',
  '--secondary-foreground': 'oklch(0.92 0.01 235)',
  '--muted': 'oklch(0.35 0.02 235)',
  '--muted-foreground': 'oklch(0.65 0.02 235)',
  '--accent': 'oklch(0.72 0.12 195)',
  '--accent-foreground': 'oklch(0.12 0.03 235)',
  '--border': 'oklch(0.28 0.025 235)',
  '--input': 'oklch(0.22 0.025 235)',
  '--ring': 'oklch(0.78 0.15 168)',
}

/**
 * Background wrapper for auth pages with ghostly mist effect.
 * Always forces dark mode regardless of user's theme preference.
 */
export function AuthBackground({ children }: AuthBackgroundProps) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={darkThemeVars}
    >
      {/* Base background with mist SVG */}
      <div
        className="absolute inset-0 bg-[#0d1b2a]"
        style={{
          backgroundImage: `
            url('/bg-banshee-mist.svg'),
            radial-gradient(ellipse at center, #0b1a24 0%, #05090f 70%)
          `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Ultra-subtle noise overlay for texture */}
      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Radial glow behind the center card */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, rgba(127,255,228,0.06) 0%, transparent 50%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full flex justify-center">
        {children}
      </div>
    </div>
  )
}
